import React, { useEffect, useState } from "react";
import { ShieldOff, ShieldCheck, Undo2, XCircle, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useAuth } from "../contexts/AuthContext";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { effectivenessState } from "../utils/aiRespondRows";

const ACTION_LABEL = {
  DisableEntraIdentity: "Disable Entra identity",
  QuarantineAwsRole: "Quarantine AWS role",
  DisableAnthropicWorkspaceKeys: "Deactivate Anthropic workspace keys",
  DeleteOpenAiProjectKeys: "Delete OpenAI project keys",
  DisableGcpServiceAccount: "Disable Google Cloud service account",
};

// A permanent action must read as one everywhere the operator decides — the request form, both
// confirm dialogs, and the absence of a Reverse button afterwards. `supportsReversal` comes from
// the backend on both available actions and action rows; only an explicit false is irreversible.
const reversibilitySentence = (action) =>
  action?.supportsReversal === false
    ? "THIS ACTION IS PERMANENT — deleted keys cannot be restored; new keys must be created in the provider's console."
    : "The action is reversible from this panel.";

// Every entry the server lists applies to the asset; only `isAvailable === true` may be requested.
// Membership in the list never means "usable" — fail closed when the flag is missing.
const isActionAvailable = (a) => a?.isAvailable === true;

// Approve and Retry execute, so the server says per action row whether they would run now
// (`canExecute`, AX-280), using the same check the approve and retry routes apply. Fail closed:
// anything but an explicit true hides the button. Reject, Cancel and Reverse are never gated.
const canExecuteAction = (a) => a?.canExecute === true;
const RETRYABLE = ["ExecuteFailed", "PartiallyExecuted"];

// The row wears its state, not just the chip: a page of white boxes made "already handled" and
// "live weapon" read identically at a glance. Terminal-neutral states (rejected, cancelled,
// reversed) stay quiet; anything awaiting someone is amber, anything failed is red, and a
// provider-confirmed (Verified) action is green. What it means for issued credentials is its
// effectiveness line, not the row colour.
const STATUS_ROW = {
  Requested: "border-amber-200 bg-amber-50/70",
  Approved: "border-blue-200 bg-blue-50/60",
  Executed: "border-blue-200 bg-blue-50/60",
  Verified: "border-emerald-200 bg-emerald-50/60",
  ExecuteFailed: "border-red-200 bg-red-50/60",
  PartiallyExecuted: "border-amber-300 bg-amber-50/70",
  VerifyFailed: "border-red-200 bg-red-50/60",
  Reverted: "border-red-200 bg-red-50/60",
  Rejected: "border-border bg-theme-secondary",
  Cancelled: "border-border bg-theme-secondary",
  Reversed: "border-border bg-theme-secondary",
};

const STATUS_CHIP = {
  Requested: "bg-amber-50 text-amber-700 border-amber-200",
  Approved: "bg-blue-50 text-blue-700 border-blue-200",
  Executed: "bg-blue-50 text-blue-700 border-blue-200",
  Verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ExecuteFailed: "bg-red-50 text-red-700 border-red-200",
  PartiallyExecuted: "bg-amber-50 text-amber-800 border-amber-300",
  VerifyFailed: "bg-red-50 text-red-700 border-red-200",
  Rejected: "bg-gray-100 text-gray-600 border-gray-200",
  Cancelled: "bg-gray-100 text-gray-600 border-gray-200",
  Reversed: "bg-gray-100 text-gray-600 border-gray-200",
  Reverted: "bg-red-50 text-red-700 border-red-200",
};

/**
 * Response actions (Phase 5 stop actions) for one AI asset: request → risk-manager approval
 * (typed confirm, separation of duties — the backend refuses self-approval) → executed →
 * verified, with the action history as the audit trail. Verification updates the action only;
 * the linked risk closes when the next scan's evidence reflects containment.
 */
const DeclaredBehaviorLine = ({ declared }) => declared ? (
  <div className="text-[11px] text-blue-800">
    Expected behavior:{declared.models ? ` models ${declared.models.join(", ")};` : ""}
    {declared.resources ? ` resources ${declared.resources.join(", ")};` : ""}
    {declared.originCountries ? ` auth origins ${declared.originCountries.join(", ")}` : ""}
  </div>
) : null;

export default function AiResponseActionsPanel({
  assetId, assetName, available = [], actions = [], onChanged, overrideAllowed = true, irreversibleAllowed = false, showHeader = true,
}) {
  const apiCall = useApi();
  const { user } = useAuth();
  const { canManageOrganizationRisks } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const orgId = org?.organizationId;
  // One uniform gate for the whole module (RISK_MANAGER+ at ORG scope or above). The four-eyes
  // control is server-side separation of duties (decider ≠ requester), not a role split.
  const canRequest = canManageOrganizationRisks(orgId);
  const canDecide = canManageOrganizationRisks(orgId);
  // Separation of duties (AX-295): the requester can cancel but never approve, reject or retry.
  const isRequester = (a) => String(a.requestedByUserId) === String(user?.id);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // { kind: "approve" | "reverse", action } — drives the typed-confirm modal
  const [confirming, setConfirming] = useState(null);
  // actionType whose inline request form is open, plus its inputs. The approver is required:
  // they are emailed and get the My Tasks entry, though any other risk manager may still decide.
  const [requesting, setRequesting] = useState(null);
  const [approvers, setApprovers] = useState([]);
  // The approver list depends on the asset (a private-deal asset lists only that deal's
  // members), so it is fetched per asset and dropped when the asset changes.
  const [approversFor, setApproversFor] = useState(null);
  const [approverId, setApproverId] = useState("");
  const [reason, setReason] = useState("");
  // { available, preflight } for the open request form, or "loading". The impact statement is
  // required before anyone can submit: a request whose blast radius cannot be read is refused
  // server-side, so the form says so up front instead of failing on submit.
  const [preflight, setPreflight] = useState(null);
  // "approval" routes to a second person; "immediate" dissolves that check deliberately, so it
  // demands a reason and a stronger confirmation.
  const [mode, setMode] = useState("approval");

  // A different asset: drop the per-asset approver list and close any open request form, whose
  // approver and impact statement belonged to the previous asset.
  useEffect(() => {
    setApprovers([]);
    setApproversFor(null);
    setApproverId("");
    setRequesting(null);
    setPreflight(null);
  }, [assetId]);

  const call = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || "The request failed — try again or check the diagnostic.");
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  const openRequestForm = async (actionType) => {
    setRequesting(actionType);
    setApproverId("");
    setReason("");
    setMode("approval");
    setPreflight("loading");
    if (approversFor !== assetId || approvers.length === 0) {
      apiCall("GET", `/ai-assets/response-actions/eligible-approvers?assetId=${encodeURIComponent(assetId)}`)
        .then((list) => {
          setApprovers(Array.isArray(list) ? list : []);
          setApproversFor(assetId);
        })
        .catch(() => {});
    }
    const result = await apiCall(
      "GET",
      `/ai-assets/response-actions/preflight/${assetId}?actionType=${encodeURIComponent(actionType)}`
    ).catch(() => null);
    setPreflight(result || { available: false, preflight: null });
  };
  const submitRequest = (actionType) =>
    call(async () => {
      try {
        await apiCall("POST", "/ai-assets/response-actions", {
          aiAssetId: assetId,
          actionType,
          approverUserId: parseInt(approverId),
          reason: reason.trim() || null,
          // The playbook state the form showed (0 = confirmed there was none). The server
          // refuses if it moved since — the version stamped on the action must be what was
          // actually seen.
          confirmedPlaybookVersion: preflight?.playbook?.version ?? 0,
        });
        setRequesting(null);
      } catch (e) {
        // A refusal (scope or playbook changed) re-reads the preflight in place, so the form
        // shows what the operator would now be confirming rather than the stale statement.
        if (e?.response?.status === 409) {
          const result = await apiCall(
            "GET",
            `/ai-assets/response-actions/preflight/${assetId}?actionType=${encodeURIComponent(actionType)}`
          ).catch(() => null);
          setPreflight(result || { available: false, preflight: null });
        }
        throw e;
      }
    });

  // Sends the target ids of the impact statement the operator confirmed; the server refuses if
  // a fresh provider read differs. On refusal the preflight is re-read in place, so the form
  // shows what would actually happen now instead of the stale statement the refusal was about.
  const submitImmediate = async (action) => {
    setBusy(true);
    setError(null);
    try {
      await apiCall("POST", "/ai-assets/response-actions/immediate", {
        aiAssetId: assetId,
        actionType: action.actionType,
        reason: reason.trim(),
        confirmedTargetIds: (preflight?.preflight?.targets || []).map((t) => t.id),
        confirmedPlaybookVersion: preflight?.playbook?.version ?? 0,
      });
      setRequesting(null);
      await onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || "The request failed — try again or check the diagnostic.");
      const result = await apiCall(
        "GET",
        `/ai-assets/response-actions/preflight/${assetId}?actionType=${encodeURIComponent(action.actionType)}`
      ).catch(() => null);
      setPreflight(result || { available: false, preflight: null });
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };
  const decide = (id, verb) =>
    call(() => apiCall("POST", `/ai-assets/response-actions/${id}/${verb}`, {}));

  const openActionTypes = new Set(
    actions
      .filter((a) => ["Requested", "Approved", "ExecuteFailed", "Executed", "VerifyFailed", "Verified"].includes(a.status))
      .map((a) => a.actionType)
  );
  const requestable = available.filter((a) => isActionAvailable(a) && !openActionTypes.has(a.actionType));
  const unavailable = available.filter((a) => !isActionAvailable(a) && !openActionTypes.has(a.actionType));
  const summaryByType = new Map(
    available.filter((a) => a.effectivenessSummary).map((a) => [a.actionType, a.effectivenessSummary])
  );

  // Hidden entirely when there is nothing to show or do: most assets have no response surface,
  // and an empty "Response" section on every asset would be noise.
  if (available.length === 0 && actions.length === 0) return null;

  return (
    <div>
      {showHeader && (
        <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">
          Response
        </div>
      )}
      <div className="space-y-2">
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {error}
          </div>
        )}

        {canRequest && requestable.map((a) => (
          <div key={a.actionType} className="border border-border rounded-md p-2.5 bg-theme-primary">
            <div className="flex items-center gap-2">
              <ShieldOff className="w-4 h-4 text-red-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-content-primary">
                  {ACTION_LABEL[a.actionType] || a.actionType}
                </div>
                <div className="text-xs text-content-secondary truncate" title={a.description}>
                  {a.description}
                </div>
              </div>
              <button
                onClick={() => (requesting === a.actionType ? setRequesting(null) : openRequestForm(a.actionType))}
                disabled={busy}
                className="text-xs px-2.5 py-1 border rounded-lg font-medium transition-all bg-red-50 text-red-700 border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 active:scale-95 disabled:opacity-50"
              >
                {requesting === a.actionType ? "Close" : "Request…"}
              </button>
            </div>
            {requesting === a.actionType && (
              <div className="mt-2 pt-2 border-t border-border space-y-2">
                {a.supportsReversal === false && (
                  <div className="flex items-start gap-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-2.5 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      <b>This action is permanent.</b> It cannot be reversed and always requires a
                      second person&apos;s approval — deleted keys cannot be restored, and the
                      approver will be told the same.
                    </span>
                  </div>
                )}

                {/* The impact statement — a live provider read of what exactly would be touched.
                    Without it nobody can submit: an approval only means something when the
                    approver saw the same list the execution is bound to. */}
                {preflight === "loading" ? (
                  <div className="text-xs text-content-secondary">Reading the current impact from the provider…</div>
                ) : preflight?.available && preflight?.preflight ? (
                  <>
                  <div className="text-xs text-content-primary bg-theme-secondary border border-border rounded-md px-2.5 py-2">
                    <span className="font-semibold">Impact: </span>
                    {preflight.preflight.summary}
                  </div>
                  {/* What the action does to credentials already issued — read before approving,
                      not discovered afterwards. Static per action type; no provider call. */}
                  {a.effectivenessSummary && (
                    <div className="text-xs text-content-primary bg-theme-secondary border border-border rounded-md px-2.5 py-2">
                      <span className="font-semibold">Effect on issued credentials: </span>
                      {a.effectivenessSummary}
                    </div>
                  )}
                  {/* The asset's playbook at the moment of decision (§11h) — the whole point of
                      filling one in. Read-only and informational; absent playbook renders
                      nothing, never a nag. */}
                  {preflight.playbook && (
                    <div className="text-xs bg-blue-50 border border-blue-200 rounded-md px-2.5 py-2 space-y-1">
                      <div className="font-semibold text-blue-900">
                        Playbook (v{preflight.playbook.version}
                        {preflight.playbook.author ? `, ${preflight.playbook.author}` : ""})
                      </div>
                      {(preflight.playbook.answers || [])
                        .filter((ans) => ["notify", "containBrakes", "recovery"].includes(ans.id))
                        .map((ans) => (
                          <div key={ans.id} className="text-blue-900">
                            <span className="font-medium">{ans.prompt}</span> {ans.answer}
                          </div>
                        ))}
                      <DeclaredBehaviorLine declared={preflight.playbook.declaredBehavior} />
                      <div className="text-[11px] text-blue-700">
                        Full document on the asset&apos;s Playbook tab. This version is recorded
                        with the request.
                      </div>
                    </div>
                  )}
                  </>
                ) : (
                  <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    {/* The server's reason names the actual obstacle (archived project, unreachable
                        provider, …) — only fall back to the generic connector-diagnostic pointer
                        when it gave none. */}
                    <span>
                      {preflight?.unavailableReason
                        ? `The action cannot be requested right now. ${preflight.unavailableReason}`
                        : "The action's impact could not be read from the provider, so it cannot be " +
                          "requested right now. Check the connector diagnostic and reopen this form."}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={mode === "approval"} onChange={() => setMode("approval")} />
                    <span className="text-content-primary">Send to an approver</span>
                  </label>
                  {overrideAllowed && (a.supportsReversal !== false || irreversibleAllowed) && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={mode === "immediate"} onChange={() => setMode("immediate")} />
                      <span className="text-content-primary">Execute immediately</span>
                    </label>
                  )}
                </div>

                {mode === "approval" ? (
                  <div>
                    <label className="block text-xs font-medium text-content-secondary mb-1">
                      Approver <span className="text-red-500">*</span>
                      <span className="font-normal"> — notified by email and My Tasks; any other risk manager may also decide</span>
                    </label>
                    <select
                      value={approverId}
                      onChange={(e) => setApproverId(e.target.value)}
                      className="w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-theme-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select an approver…</option>
                      {approvers
                        .filter((u) => String(u.id) !== String(user?.id))
                        .map((u) => (
                          <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                        ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      <b>No one else will review this.</b> The action runs against the provider as soon
                      as you confirm. Your name, the reason and the outcome are recorded on the asset's
                      activity trail, and the other risk managers in this organization are notified.
                    </span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-content-secondary mb-1">
                    Reason {mode === "immediate" && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    placeholder={mode === "immediate"
                      ? "Required — why this cannot wait for approval…"
                      : "Why this identity should be contained…"}
                    className="w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-theme-primary text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {mode === "approval" ? (
                  <button
                    onClick={() => submitRequest(a.actionType)}
                    disabled={busy || !approverId || !preflight?.available}
                    className="text-xs px-2.5 py-1 border rounded-lg font-medium bg-red-50 text-red-700 border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 active:scale-95 disabled:opacity-50"
                  >
                    Submit for approval
                  </button>
                ) : (
                  <button
                    // Available-action entries carry no targetName, so the dialog names the asset (AX-306).
                    onClick={() => setConfirming({ kind: "immediate", action: { ...a, targetName: assetName ?? a.targetIdentifier } })}
                    disabled={busy || !reason.trim() || !preflight?.available}
                    className="text-xs px-2.5 py-1 border rounded-lg font-medium bg-red-600 text-white border-red-600 hover:bg-red-700 active:scale-95 disabled:opacity-50"
                  >
                    Execute now
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {/* An action that applies to the asset but cannot be used now: named, with the server's
            reason, and no Request button. */}
        {canRequest && unavailable.map((a) => (
          <div
            key={a.actionType}
            data-testid={`unavailable-action-${a.actionType}`}
            className="border border-border rounded-md p-2.5 bg-theme-secondary opacity-80"
          >
            <div className="flex items-start gap-2">
              <ShieldOff className="w-4 h-4 text-content-secondary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-content-secondary">
                  {ACTION_LABEL[a.actionType] || a.actionType}
                </div>
                <div className="text-xs text-content-secondary">
                  {a.unavailableReason || "This action is not available for this asset right now."}
                </div>
              </div>
            </div>
          </div>
        ))}

        {actions.map((a) => (
          <div key={a.id} className={`border rounded-md p-2.5 ${STATUS_ROW[a.status] || "border-border bg-theme-primary"}`}>
            <div className="flex items-center gap-2 text-sm">
              <StatusIcon status={a.status} />
              <span className="font-medium text-content-primary">
                {ACTION_LABEL[a.actionType] || a.actionType}
              </span>
              <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium border ${STATUS_CHIP[a.status] || STATUS_CHIP.Cancelled}`}>
                {a.status}
              </span>
              {a.executedWithoutApproval && (
                <span
                  title="Executed immediately by its requester — no second person reviewed it"
                  className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
                >
                  No approval
                </span>
              )}
              <span className="text-content-secondary text-xs ml-auto">
                {a.requestedByUserName || "—"}
              </span>
            </div>

            {a.status === "Requested" && a.approverUserName && (
              <div className="mt-1.5 text-xs text-content-secondary">
                Awaiting approval by <span className="font-medium">{a.approverUserName}</span>
                {canDecide ? " — as a risk manager, you may also decide it." : "."}
              </div>
            )}
            {/* The impact snapshotted at request time — what an approval covers, and all it
                covers. Shown while the decision is open; afterwards it lives in the audit trail. */}
            {/* The instructions ATTACHED to the request — the immutable version the requester
                confirmed, rendered where the decision is made. Distinct from the current
                playbook when a newer one exists. */}
            {a.status === "Requested" && a.playbook && (
              <div className="mt-1.5 text-xs bg-blue-50 border border-blue-200 rounded-md px-2.5 py-2 space-y-1">
                <div className="font-semibold text-blue-900">
                  Playbook attached to this request (v{a.playbook.version}
                  {a.playbook.author ? `, ${a.playbook.author}` : ""})
                </div>
                {(a.playbook.answers || [])
                  .filter((ans) => ["notify", "containBrakes", "recovery"].includes(ans.id))
                  .map((ans) => (
                    <div key={ans.id} className="text-blue-900">
                      <span className="font-medium">{ans.prompt}</span> {ans.answer}
                    </div>
                  ))}
                <DeclaredBehaviorLine declared={a.playbook.declaredBehavior} />
                {a.playbookNewerExists && (
                  <div className="text-[11px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200 inline-block">
                    These are the instructions attached to this request — a newer current playbook
                    exists on the asset&apos;s Playbook tab; review it before deciding.
                  </div>
                )}
              </div>
            )}
            {a.status === "Requested" && a.playbookVersion != null && !a.playbook && (
              <div className="text-[11px] text-content-secondary">
                Playbook v{a.playbookVersion} informed this request.
                {a.playbookNewerExists && (
                  <span className="ml-1 px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">
                    a newer playbook now exists — review it before deciding
                  </span>
                )}
              </div>
            )}
            {a.status === "Requested" && a.approvedScope?.summary && (
              <div className="mt-1.5 text-xs text-content-primary bg-theme-secondary border border-border rounded-md px-2.5 py-2">
                <span className="font-semibold">Approved scope: </span>
                {a.approvedScope.summary}
              </div>
            )}
            {/* The approver reads the same effect on issued credentials the requester did, from
                the unfiltered available list. No matching entry → nothing, never a guess. */}
            {a.status === "Requested" && summaryByType.get(a.actionType) && (
              <div className="mt-1.5 text-xs text-content-primary bg-theme-secondary border border-border rounded-md px-2.5 py-2">
                <span className="font-semibold">Effect on issued credentials: </span>
                {summaryByType.get(a.actionType)}
              </div>
            )}
            {a.failureDetail && (
              <div className="mt-1.5 text-xs text-red-700">{a.failureDetail}</div>
            )}
            {a.status === "Verified" && a.verifiedAt && (
              <div className="mt-1.5 text-xs text-content-secondary">
                Verified against the provider {new Date(a.verifiedAt).toLocaleString()} — the linked
                risk closes when the next scan reflects containment.
              </div>
            )}
            <EffectivenessLine state={effectivenessState(a)} />

            {/* The requester never decides their own request (AX-295), so a note about whether
                it can be approved or retried now is not theirs to act on. */}
            {canDecide && !isRequester(a) && (a.status === "Requested" || RETRYABLE.includes(a.status)) && !canExecuteAction(a) && (
              <div className="mt-1.5 text-xs text-amber-800" data-testid={`execute-unavailable-${a.id}`}>
                {a.status === "Requested" ? "Cannot be approved now" : "Cannot be retried now"}:{" "}
                {a.executeUnavailableReason || "This action is not available for this asset right now."}
              </div>
            )}

            <div className="mt-2 flex items-center gap-2">
              {a.status === "Requested" && canDecide && isRequester(a) && (
                <span className="text-xs text-content-secondary" data-testid={`awaiting-approver-${a.id}`}>
                  Waiting for another approver
                </span>
              )}
              {a.status === "Requested" && canDecide && !isRequester(a) && (
                <>
                  {canExecuteAction(a) && (
                    <button onClick={() => setConfirming({ kind: "approve", action: a })} disabled={busy}
                            className="text-xs px-2.5 py-1 border rounded-lg font-medium bg-red-50 text-red-700 border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 active:scale-95 disabled:opacity-50">
                      Approve &amp; execute
                    </button>
                  )}
                  <button onClick={() => decide(a.id, "reject")} disabled={busy}
                          className="text-xs px-2.5 py-1 border rounded-lg font-medium bg-theme-secondary text-content-secondary border-border hover:bg-theme-hover active:scale-95 disabled:opacity-50">
                    Reject
                  </button>
                </>
              )}
              {a.status === "Requested" && canDecide && isRequester(a) && (
                <button onClick={() => decide(a.id, "cancel")} disabled={busy}
                        className="text-xs px-2.5 py-1 border rounded-lg font-medium bg-theme-secondary text-content-secondary border-border hover:bg-theme-hover active:scale-95 disabled:opacity-50">
                  Cancel request
                </button>
              )}
              {RETRYABLE.includes(a.status) && canDecide && canExecuteAction(a) && !isRequester(a) && (
                <button onClick={() => decide(a.id, "retry")} disabled={busy}
                        className="text-xs px-2.5 py-1 border rounded-lg font-medium bg-red-50 text-red-700 border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 active:scale-95 disabled:opacity-50">
                  {a.status === "PartiallyExecuted" ? "Retry remaining" : "Retry execution"}
                </button>
              )}
              {["Executed", "Verified", "VerifyFailed", "PartiallyExecuted"].includes(a.status) && canDecide && a.supportsReversal !== false && (
                <button onClick={() => setConfirming({ kind: "reverse", action: a })} disabled={busy}
                        className="flex items-center gap-1 text-xs px-2.5 py-1 border rounded-lg font-medium bg-theme-secondary text-content-secondary border-border hover:bg-theme-hover active:scale-95 disabled:opacity-50">
                  <Undo2 className="w-3 h-3" />
                  Reverse
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDeleteModal
        isOpen={!!confirming}
        onClose={() => setConfirming(null)}
        isBusy={busy}
        title={
          confirming?.kind === "reverse" ? "Reverse response action"
            : confirming?.kind === "immediate" ? "Execute without approval"
            : "Approve response action"
        }
        confirmLabel={
          confirming?.kind === "reverse" ? "Reverse"
            : confirming?.kind === "immediate" ? "Execute now"
            : "Approve & execute"
        }
        message={
          // A Google Cloud reverse can succeed as a no-op (the account was already disabled, or
          // another active containment needs it disabled), so it must not promise re-enabling.
          confirming?.kind === "reverse" && confirming?.action?.actionType === "DisableGcpServiceAccount"
            ? `This reverses the containment on "${confirming?.action?.targetName}". Axaris re-enables the service account only if this action disabled it and no other active containment still needs it disabled; otherwise the account stays disabled.`
            : confirming?.kind === "reverse"
            ? `This undoes the containment on "${confirming?.action?.targetName}" — the identity becomes active again and any closed risk will re-open on the next scan.`
            : confirming?.kind === "immediate"
            ? `You are executing this without a second person reviewing it:\n\n${ACTION_LABEL[confirming?.action?.actionType] || confirming?.action?.actionType} on "${confirming?.action?.targetName}" (${confirming?.action?.targetIdentifier}).\n\nReason: ${reason.trim()}\n\nIt runs against the provider immediately. Your name, the reason and the outcome are recorded, and the organization's other risk managers are notified. ${reversibilitySentence(confirming?.action)}`
            : `This executes immediately against the provider:\n\n${ACTION_LABEL[confirming?.action?.actionType] || confirming?.action?.actionType} on "${confirming?.action?.targetName}" (${confirming?.action?.targetIdentifier}).\n\n${confirming?.action?.approvedScope?.summary ? `Approved scope: ${confirming.action.approvedScope.summary}\n\n` : ""}Axaris will verify the effect by re-reading the provider. ${reversibilitySentence(confirming?.action)}`
        }
        onConfirm={() =>
          confirming.kind === "immediate"
            ? submitImmediate(confirming.action)
            : decide(confirming.action.id, confirming.kind === "reverse" ? "reverse" : "approve")
        }
      />
    </div>
  );
}

// One verified action's effect on credentials issued before it ran (see effectivenessState).
function EffectivenessLine({ state }) {
  if (!state) return null;
  return (
    <div className="mt-1 text-xs text-content-secondary space-y-0.5">
      <div>
        <span className="font-semibold text-content-primary">{state.label}</span>
        {state.detail && <> — {state.detail}</>}
      </div>
      {state.basis && <div>{state.basis}</div>}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "Verified") return <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />;
  if (status === "Requested") return <Clock className="w-4 h-4 text-amber-500 shrink-0" />;
  if (status === "PartiallyExecuted") return <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />;
  if (["ExecuteFailed", "VerifyFailed", "Reverted"].includes(status))
    return <XCircle className="w-4 h-4 text-red-500 shrink-0" />;
  if (["Approved", "Executed"].includes(status))
    return <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0" />;
  return <Undo2 className="w-4 h-4 text-gray-400 shrink-0" />;
}
